import React, { useMemo, memo, useEffect, useState, useRef } from 'react';
import Chart from 'react-apexcharts';
import { useApexTheme, mergeThemeOptions } from './apexTheme';

/**
 * Optimized ApexChart wrapper that handles theme changes efficiently.
 * 
 * Features:
 * - Shows loading spinner during chart rendering (not just data fetch)
 * - Delays chart render to allow spinner animation to start smoothly
 * - Measures and logs chart render performance
 */
const OptimizedChart = memo(({
    componentOptions,
    series,
    type,
    height,
    chartName = 'Chart'
}) => {
    const { baseThemeOptions, isDark } = useApexTheme();
    const [isRendering, setIsRendering] = useState(true);
    const [shouldRenderChart, setShouldRenderChart] = useState(false);
    const renderStartTimeRef = useRef(performance.now());

    // Memoize the full options
    const fullOptions = useMemo(() => {
        if (!componentOptions) return null;
        return mergeThemeOptions(baseThemeOptions, componentOptions);
    }, [componentOptions, baseThemeOptions]);

    // Delay chart rendering to let spinner animate smoothly first
    useEffect(() => {
        if (fullOptions && series?.length > 0) {
            setIsRendering(true);
            setShouldRenderChart(false);

            // Use requestAnimationFrame to ensure spinner is painted first
            requestAnimationFrame(() => {
                // Then wait one more frame for spinner animation to start
                requestAnimationFrame(() => {
                    renderStartTimeRef.current = performance.now();
                    console.log(`⏱️ ${chartName} starting render...`);
                    setShouldRenderChart(true);
                });
            });
        }
    }, [fullOptions, series, chartName]);

    // ApexCharts fires 'mounted' event when chart is ready
    const chartOptions = useMemo(() => {
        if (!fullOptions) return null;

        return {
            ...fullOptions,
            chart: {
                ...fullOptions.chart,
                events: {
                    ...fullOptions.chart?.events,
                    mounted: function () {
                        setIsRendering(false);
                        const renderTime = performance.now() - renderStartTimeRef.current;
                        console.log(`📊 ${chartName} render time: ${renderTime.toFixed(2)}ms`);
                    },
                    updated: function () {
                        setIsRendering(false);
                    }
                }
            }
        };
    }, [fullOptions, chartName]);

    // Early return for no data
    if (!chartOptions || !series || series.length === 0) {
        return (
            <div className="flex items-center justify-center text-base-content/60" style={{ height }}>
                No data available
            </div>
        );
    }

    return (
        <div className="relative" style={{ minHeight: height }}>
            {/* Loading overlay during chart rendering */}
            {isRendering && (
                <div
                    className="absolute inset-0 flex flex-col justify-center items-center bg-base-100 z-10"
                    style={{ height }}
                >
                    <span className="loading loading-spinner loading-lg text-info"></span>
                    <span className="mt-2 text-sm text-base-content/60">Rendering {chartName}...</span>
                </div>
            )}

            {/* Only render chart after spinner has painted */}
            {shouldRenderChart && (
                <Chart
                    key={`chart-${isDark ? 'dark' : 'light'}`}
                    options={chartOptions}
                    series={series}
                    type={type}
                    height={height}
                />
            )}
        </div>
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.height === nextProps.height &&
        prevProps.type === nextProps.type &&
        prevProps.chartName === nextProps.chartName &&
        JSON.stringify(prevProps.series) === JSON.stringify(nextProps.series) &&
        JSON.stringify(prevProps.componentOptions) === JSON.stringify(nextProps.componentOptions)
    );
});

OptimizedChart.displayName = 'OptimizedChart';

export default OptimizedChart;
